<template>
  <!-- -------------------------------------------------------- -->
  <!-- 商品×店舗 -->
  <!-- 参考 商品×KPI(QueryChocostickI.vue) -->
  <!-- -------------------------------------------------------- -->
  <long-query-layout
    :resource-name="resourceName"
    :query-filter="queryFilter"
    :map-query-filter-for-api="mapQueryFilterForApi"
    :apply-query-filter="applyQueryFilter"
    :result-fetch-url.sync="resultTable.url"
    :columns.sync="resultTable.columns"
    :group-headers.sync="resultTable.groupHeaders"
    :active-tab.sync="activeTab"
    :excel-properties-handler="excelPropertiesHandler.bind(this)"
    :convert-menu-default-query-filter="convertMenuDefaultQueryFilter"
    @result-area-size-change="updateTableSize"
    @query-completed="onQueryCompleted"
    @query-id-change="queryId = $event"
    ref="parentLayout"
  >
    <!-- -------------------------------------------------------- -->
    <!-- 分析条件 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:filter>
      <!-- 期間 -->
      <query-filter-element id="term" title="期間" contentWidth="medium">
        <filter-term v-bind.sync="queryFilter.term" :default-week-count="8" />
      </query-filter-element>

      <!-- 商品分類 -->
      <query-filter-element id="category-item-category" title="商品分類">
        <gl-select-company :company.sync="queryFilter.item.company" />
        <filter-category-item-category
          :company="queryFilter.item.company"
          v-bind.sync="queryFilter.item"
          :pos-restriction="true"
          :use-new-item-component="true"
        />
      </query-filter-element>

      <!-- 店舗 -->
      <query-filter-element id="store" title="店舗">
        <filter-store v-bind.sync="queryFilter.store" />
      </query-filter-element>

      <!-- オプション -->
      <query-filter-element id="option-marmot-i" title="オプション">
        <filter-option-pos v-bind.sync="queryFilter.option" />
      </query-filter-element>
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析結果 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:result>
      <div
        :gl-tooltip="
          !$store.state.isQueryFullFeatured
            ? msg.MSG_QUERY_FULL_FEATURE_UNAVAILABLE
            : ''
        "
        gl-tooltip-direction="left"
        class="button-group button-group__layout"
      >
        <md-button
          v-for="level in itemCategoryLevels"
          class="md-raised button-group__button"
          :value="level.level"
          :key="level.level"
          :disabled="!$store.state.isQueryFullFeatured"
          @click="drilldown(level.level)"
          >{{ level.name }}へ
        </md-button>
      </div>

      <gl-table ref="myTable" v-bind="resultTable" />

      <md-snackbar
        md-position="center"
        :md-duration="4000"
        :md-active.sync="showSnackbar"
        md-persistent
      >
        <span>対象の分類を一覧から選択してください</span>
      </md-snackbar>
    </template>
  </long-query-layout>
</template>

<script>
// 分析条件, 分析結果のためのレイアウト
import LongQueryLayout from '@/components/layout/LongQueryLayout.vue'
// 分析条件
import QueryFilterElement from '@/components/QueryFilterElement.vue'
import FilterTerm from '@/components/FilterTerm.vue'
import FilterCategoryItemCategory from '@/components/FilterCategoryItemCategory.vue'
import FilterStore from '@/components/FilterStore.vue'
import FilterOptionPos from '@/components/FilterOptionMarmotI.vue'
// 分析条件モデル
import term from '@/queryfilter/term'
import categoryItemCategory from '@/queryfilter/categoryItemCategory2'
import store from '@/queryfilter/store'
// 分析結果
import GlTable from '@/components/GlTable.vue'
import {
  standardTableCustomFunction,
  standardWithSearchCustomFunction,
} from '@/util/custom_formatters'
import msg from '@/const/messages'

export default {
  components: {
    QueryFilterElement,
    FilterTerm,
    FilterCategoryItemCategory,
    FilterStore,
    FilterOptionPos,
    LongQueryLayout,
    GlTable,
  },
  data() {
    return {
      msg,
      resourceName: 'marmot-i',
      bottomItemCategoryLevel: undefined,
      showSnackbar: false,
      /**
       * ドリルダウン先の階層
       */
      itemCategoryLevels: [],

      queryId: undefined,
      /**
       * 表示タブ（分析条件、分析結果）
       *
       * 表示タブをプログラム側から切り替えたい場合をこれを使います。
       */
      activeTab: undefined,
      /**
       * 分析条件
       */
      queryFilter: {
        term: { order: 1, ...term.get() },
        item: {
          order: 2,
          summaryKey: 'categoryItemCategory',
          enableFilterNewItems: true,
          ...categoryItemCategory.get(),
        },
        store: {
          order: 3,
          //  選択可能店舗数設定
          //  店舗が列表示の分析のため、現実的に表示ができる店舗数に制限するための設定
          storeCountMax: process.env.VUE_APP_STORE_COUNT_MAX
            ? Number(process.env.VUE_APP_STORE_COUNT_MAX)
            : 200,
          ...store.get(),
        },
        option: {
          order: 4,
          summaryKey: 'optionMarmotI',
          evaluation: 'amount',
          errors: [],
        },
        drilldown: {
          queryId: undefined,
        },
      },
      /**
       * 分析結果
       */
      resultTable: {
        columns: undefined,
        groupHeaders: undefined,
        sortColumnName: 'code',
        sortOrder: 'asc',
        selectable: true,
        url: undefined,
        width: 0,
        height: 0,
        customFormatter: {
          standard: standardTableCustomFunction,
          linkifySearch: standardWithSearchCustomFunction,
        },
      },
      /**
       * この画面が持つ分析条件を、クエリのペイロードに変換する
       */
      mapQueryFilterForApi: queryFilter => ({
        term: term.mapToApiPayload(queryFilter.term),
        category:
          queryFilter.item.selectMode === 'category'
            ? categoryItemCategory.mapToApiPayload(queryFilter.item)
            : null,
        item:
          queryFilter.item.selectMode === 'category'
            ? null
            : categoryItemCategory.mapToApiPayload(queryFilter.item),
        store: store.mapToApiPayload(queryFilter.store),
        option: {
          evaluation: queryFilter.option.evaluation,
        },
        drilldown: queryFilter.drilldown,
      }),
      /**
       * 渡された分析条件をこの画面の分析条件に反映する
       */
      applyQueryFilter: queryFilter => {
        term.applyPayload(this.queryFilter.term, queryFilter.term)
        categoryItemCategory.applyPayload(
          this.queryFilter.item,
          queryFilter.item,
        )
        store.applyPayload(this.queryFilter.store, queryFilter.store)
        this.queryFilter.option.evaluation =
          queryFilter.option.evaluation || 'amount'
        if (queryFilter.drilldown) {
          this.queryFilter.drilldown.queryId = undefined
        }
      },
      /**
       * Excel出力のパラメータ生成
       */
      excelPropertiesHandler() {
        const { name, order } = this.$refs.myTable.getCurrentSort()
        return {
          sortColumnName: name,
          sortOrder: order,
        }
      },
    }
  },
  watch: {
    'queryFilter.item.selectMode'(newVal) {
      this.resultTable.selectable = newVal === 'category'
    },
  },
  methods: {
    /**
     * 結果表示テーブルのサイズを更新する
     */
    updateTableSize(size) {
      this.resultTable.width = size.width
      this.resultTable.height = size.height - 50
    },
    onQueryCompleted() {
      this.loadItemCategoryLevelsData()
    },
    /**
     * 選択しているカテゴリ階層以下＋商品をロード
     */
    loadItemCategoryLevelsData() {
      if (this.queryFilter.item.selectMode === 'category') {
        const itemCategoryLevels = this.$store.state.itemCategoryLevels[
          this.queryFilter.item.company
        ]

        //商品のために最下層を取得しておく
        this.bottomItemCategoryLevel =
          itemCategoryLevels[itemCategoryLevels.length - 1].level

        //条件で選択している階層までは削除
        const deleteIndex = itemCategoryLevels.findIndex(
          i => i.level == this.queryFilter.item.category.itemCategoryLevel,
        )

        this.itemCategoryLevels = [
          ...itemCategoryLevels.slice(deleteIndex + 1),
          {
            level: null,
            name: '商品',
          },
        ]
      } else {
        this.itemCategoryLevels = []
      }
    },
    /**
     * ドリルダウン処理
     **/
    async drilldown(itemCategoryLevel) {
      const selectedRows = this.$refs.myTable.getSelectedRowData()
      const allChecked = this.$refs.myTable.isSelectAllChecked()
      if (selectedRows.length === 0 && !allChecked) {
        this.showSnackbar = true
        return
      }
      this.showSnackbar = false
      this.queryFilter.drilldown.queryId = this.queryId
      const fromItemCategoryLevel = this.queryFilter.item.category
        .itemCategoryLevel
      const toItemCategoryLevel =
        itemCategoryLevel !== null
          ? itemCategoryLevel
          : this.bottomItemCategoryLevel
      let categories = selectedRows.map(r => r.code)

      // 全選択チェックボックス入っている場合はselectedRows関係なく全行取得してドリルダウンを行う。
      if (allChecked) {
        // 全データのカテゴリコード取得
        const { name, order } = this.$refs.myTable.getCurrentSort()
        // 取得行数を99999にして、全件取得する。
        const params = {
          numberOfRows: 99999,
          currentPage: 1,
          sortColumnName: name,
          sortOrder: order,
        }
        categories = await this.$longQuery.fetchAllCategoryListForDrillDown(
          this.resourceName,
          this.queryId,
          params,
        )
      }
      const drilldownCategories = await this.getTargetDrilldownCategories(
        this.queryFilter.item.company,
        fromItemCategoryLevel,
        toItemCategoryLevel,
        categories,
      )
      if (itemCategoryLevel == null) {
        //商品へは商品(分類)へ切り替え
        this.queryFilter.item.selectMode = 'itemCategory'
        this.queryFilter.item.item.filterNewItems = this.queryFilter.item.category.filterNewItems
        this.queryFilter.item.item.categories = drilldownCategories
      } else {
        //各カテゴリへ
        this.queryFilter.item.category.itemCategoryLevel = itemCategoryLevel
        this.queryFilter.item.category.categories = drilldownCategories
      }
      this.$refs.parentLayout.executeQuery()
    },
    /**
     * fromのcategoriesよりtoのカテゴリを取得
     */
    async getTargetDrilldownCategories(company, from, to, categories) {
      const chunkSize = 100
      const targetDrilldownCategories = []
      for (let i = 0; i < categories.length; i += chunkSize) {
        const chunkCategories = categories.slice(i, i + chunkSize)
        const chunkDrilldownCategories = await this.$repositories
          .get('itemCategory')
          .getDrilldownData(company, from, to, chunkCategories)
        targetDrilldownCategories.push(...chunkDrilldownCategories)
      }
      return targetDrilldownCategories.map(c => c.code)
    },
    // デフォルトQueryFilterの変換処理
    // 商品グループは一時的なものなのでメニューのデフォルト条件
    // としては保持しない。
    convertMenuDefaultQueryFilter(queryFilter) {
      queryFilter.item.item.itemGroupId = undefined
      return queryFilter
    },
  },
}
</script>
